import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { JournalEntryWriteResponse } from '@/api/handlers/resources/journal/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { JournalQueryKeys } from './journalQueryKeys';
import type { SaveJournalDraftInput } from './useSaveJournalDraftMutation';

/**
 * Post an entry. Mirrors the real two-step contract — an entry must exist
 * server-side before it can be posted — by saving the draft and then posting it
 * by id, so the form can offer a single "Post" action.
 *
 * The save leg must be a real save: create is first-write-wins, so posting an
 * edited draft without passing the `etag` would post the *original* content.
 * Returns the save failure verbatim if that leg fails; otherwise the post
 * envelope, which carries the assigned entry number on success or the server's
 * single rejection code (`PERIOD_CLOSED`, `UNBALANCED`, …) on a 400.
 *
 * Never rejects — callers branch on `success`.
 */
export const usePostJournalEntryMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<JournalEntryWriteResponse>,
        Error,
        SaveJournalDraftInput
    >({
        mutationFn: async ({ body, etag }) => {
            const saved = await apiClient.journal.saveDraft(body, etag);
            if (!saved.success) {
                return saved;
            }
            return apiClient.journal.postEntry(body.id);
        },
        onSuccess: (result) => {
            if (result.success) {
                queryClient.setQueryData(
                    JournalQueryKeys.entry(result.response.id),
                    result,
                );
                queryClient.invalidateQueries({
                    queryKey: JournalQueryKeys.all,
                });
            }
        },
    });
};
